import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { MapPin, PlayCircle, PauseCircle, StopCircle, X, Info, Clock } from "lucide-react";

/**
 * Live clock-in / clock-out tracker (spec §9).
 * Displayed on the candidate side for a mission where they are `selected` and
 * the mission date is TODAY or PAST.
 *
 * Flow: not_started → arrival (Je suis arrivé) → optional break/reprise → departure.
 * Geolocation is opt-in per action (consent checkbox); we NEVER read it silently.
 */
export default function MissionLiveTracking({ applicationId, missionTitle }) {
    const [ts, setTs] = useState(null);
    const [loading, setLoading] = useState(true);
    const [consentGeo, setConsentGeo] = useState(false);
    const [busy, setBusy] = useState(false);
    const [showEndReport, setShowEndReport] = useState(false);
    const [endReport, setEndReport] = useState("");
    const [now, setNow] = useState(Date.now());

    useEffect(() => {
        let cancelled = false;
        const load = async () => {
            try {
                const r = await api.get(`/timesheets/live/${applicationId}`);
                if (!cancelled) setTs(r.data);
            } catch (_) { /* silent */ }
            finally { if (!cancelled) setLoading(false); }
        };
        load();
        return () => { cancelled = true; };
    }, [applicationId]);

    // Live clock (updates every second while on_site or on_break)
    useEffect(() => {
        if (!ts || ts.live_status === "departed" || ts.live_status === "not_started") return;
        const iv = setInterval(() => setNow(Date.now()), 1000);
        return () => clearInterval(iv);
    }, [ts?.live_status]);

    // Ask browser for geolocation IF and only IF user consented
    const grabGeo = () => new Promise((resolve) => {
        if (!consentGeo || !navigator.geolocation) return resolve({});
        navigator.geolocation.getCurrentPosition(
            (pos) => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
            () => resolve({}), // silent fail
            { enableHighAccuracy: false, timeout: 5000, maximumAge: 60000 },
        );
    });

    const call = async (endpoint, extraBody = {}) => {
        setBusy(true);
        try {
            const geo = await grabGeo();
            const body = { ...extraBody };
            if (geo.lat) { body.lat = geo.lat; body.lng = geo.lng; body.consent_geo = true; }
            const r = await api.post(`/timesheets/live/${applicationId}/${endpoint}`, body);
            setTs(r.data);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Erreur");
        } finally { setBusy(false); }
    };

    const arrival = () => call("arrival");
    const startBreak = () => call("break-start");
    const endBreak = () => call("break-end");
    const departure = async () => {
        await call("departure", { end_report: endReport });
        setShowEndReport(false);
        toast.success("Mission terminée. Vous pouvez maintenant déclarer vos heures définitives.");
    };

    const elapsed = useMemo(() => {
        if (!ts?.arrival_at) return "";
        const start = new Date(ts.arrival_at).getTime();
        const end = ts.departure_at ? new Date(ts.departure_at).getTime() : now;
        const seconds = Math.max(0, Math.floor((end - start) / 1000));
        const h = Math.floor(seconds / 3600).toString().padStart(2, "0");
        const m = Math.floor((seconds % 3600) / 60).toString().padStart(2, "0");
        const s = (seconds % 60).toString().padStart(2, "0");
        return `${h}:${m}:${s}`;
    }, [ts, now]);

    if (loading) return <div className="text-xs text-slate-400 py-2">Chargement du pointage…</div>;

    const status = ts?.live_status || "not_started";
    const stateBadge = {
        not_started: { color: "bg-slate-300 text-[#0B192C]", label: "Pointage à effectuer" },
        on_site: { color: "bg-emerald-500 text-white", label: "🟢 Sur place" },
        on_break: { color: "bg-amber-500 text-white", label: "☕ En pause" },
        departed: { color: "bg-slate-500 text-white", label: "🏁 Mission terminée" },
    }[status];

    return (
        <div
            className="mt-3 rounded-xl border-2 border-[#F5A623]/40 bg-gradient-to-br from-[#0B192C] to-[#142238] p-4 text-white"
            data-testid={`live-tracker-${applicationId}`}
        >
            <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
                <div>
                    <div className="overline text-[#F5A623] mb-1 flex items-center gap-1">
                        <Clock size={11} /> Pointage terrain
                    </div>
                    <div className="text-sm font-semibold">{missionTitle}</div>
                </div>
                <span
                    className={`text-[11px] font-bold px-2.5 py-1 rounded-full ${stateBadge.color}`}
                    data-testid={`live-status-${applicationId}`}
                >
                    {stateBadge.label}
                </span>
            </div>

            {ts?.arrival_at && (
                <div className="rounded-lg bg-white/5 p-3 mb-3 flex items-center justify-between gap-2">
                    <div className="text-[11px] text-white/70">
                        Arrivée : <b className="text-white">{ts.arrival_at.slice(11, 16)}</b>
                        {ts.total_break_minutes_live > 0 && (
                            <> · Pause cumulée : <b className="text-white">{ts.total_break_minutes_live} min</b></>
                        )}
                        {ts.departure_at && (
                            <> · Départ : <b className="text-white">{ts.departure_at.slice(11, 16)}</b></>
                        )}
                    </div>
                    {status !== "departed" && (
                        <div className="font-mono text-lg font-bold text-[#F5A623]">{elapsed}</div>
                    )}
                </div>
            )}

            {status === "not_started" && (
                <>
                    <label className="flex items-start gap-2 text-[11px] mb-3 text-white/80 cursor-pointer">
                        <input
                            type="checkbox"
                            checked={consentGeo}
                            onChange={(e) => setConsentGeo(e.target.checked)}
                            className="mt-0.5 accent-[#F5A623]"
                            data-testid={`live-consent-geo-${applicationId}`}
                        />
                        <span>
                            <MapPin size={11} className="inline -mt-0.5 mr-1 text-[#F5A623]" />
                            J'accepte de partager ma géolocalisation pour justifier mon pointage.
                            <span className="block text-[10px] text-white/50 mt-0.5">
                                Facultatif · vos coordonnées ne sont pas partagées avec le client.
                            </span>
                        </span>
                    </label>
                    <button
                        onClick={arrival}
                        disabled={busy}
                        className="w-full btn-gold py-3 text-sm font-bold inline-flex items-center justify-center gap-2 disabled:opacity-50"
                        data-testid={`live-arrival-${applicationId}`}
                    >
                        <PlayCircle size={18} /> Je suis arrivé
                    </button>
                </>
            )}

            {status === "on_site" && (
                <div className="grid grid-cols-2 gap-2">
                    <button
                        onClick={startBreak}
                        disabled={busy}
                        className="btn-ghost-navy !bg-white/10 !border-white/20 !text-white hover:!bg-white/20 py-3 text-sm inline-flex items-center justify-center gap-2"
                        data-testid={`live-break-start-${applicationId}`}
                    >
                        <PauseCircle size={18} /> Pause
                    </button>
                    <button
                        onClick={() => setShowEndReport(true)}
                        disabled={busy}
                        className="btn-gold py-3 text-sm inline-flex items-center justify-center gap-2 disabled:opacity-50"
                        data-testid={`live-departure-${applicationId}`}
                    >
                        <StopCircle size={18} /> Fin de mission
                    </button>
                </div>
            )}

            {status === "on_break" && (
                <button
                    onClick={endBreak}
                    disabled={busy}
                    className="w-full btn-gold py-3 text-sm inline-flex items-center justify-center gap-2 disabled:opacity-50"
                    data-testid={`live-break-end-${applicationId}`}
                >
                    <PlayCircle size={18} /> Reprendre le service
                </button>
            )}

            {status === "departed" && (
                <div className="rounded-lg bg-emerald-500/10 border border-emerald-400/40 p-3 text-xs text-white/90 flex gap-2">
                    <Info size={14} className="text-emerald-400 shrink-0 mt-0.5" />
                    <div>
                        Vous avez terminé votre mission à <b>{ts.departure_at?.slice(11, 16)}</b>.
                        Pensez à déclarer vos heures définitives via le bouton
                        « Déclarer mes heures » pour recevoir votre facture.
                    </div>
                </div>
            )}

            {showEndReport && (
                <div
                    className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-start justify-center overflow-auto py-8 px-4"
                    onClick={() => setShowEndReport(false)}
                >
                    <div
                        className="bg-white rounded-2xl w-full max-w-md shadow-2xl text-[#0B192C]"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="flex justify-between items-center px-6 py-5 border-b border-slate-200">
                            <h2 className="font-display text-xl">Terminer la mission</h2>
                            <button onClick={() => setShowEndReport(false)} className="w-9 h-9 rounded-full hover:bg-slate-100 flex items-center justify-center">
                                <X size={18} />
                            </button>
                        </div>
                        <div className="p-6 space-y-3">
                            <p className="text-sm text-slate-500">
                                Notez éventuellement un compte-rendu (incidents, points d'amélioration).
                                Facultatif — le client pourra le consulter avec sa validation d'heures.
                            </p>
                            <textarea
                                value={endReport}
                                onChange={(e) => setEndReport(e.target.value)}
                                rows={4}
                                maxLength={2000}
                                placeholder="Rapport de fin de mission…"
                                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:border-[#F5A623] outline-none resize-none"
                                data-testid={`live-end-report-${applicationId}`}
                            />
                        </div>
                        <div className="px-6 py-4 border-t border-slate-100 flex justify-end gap-2">
                            <button onClick={() => setShowEndReport(false)} className="btn-ghost-navy text-sm">
                                Annuler
                            </button>
                            <button
                                onClick={departure}
                                disabled={busy}
                                className="btn-gold text-sm disabled:opacity-50"
                                data-testid={`live-confirm-departure-${applicationId}`}
                            >
                                Confirmer la fin
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
